import ProfileCard from "@/components/profile/ProfileCard";
import ScrollTopButton from "@/components/ScrollTopButton";
import Spinner from "@/components/Spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchSortBy } from "@/generated-api";
import { useToast } from "@/hooks/use-toast";
import { useSearchProfiles } from "@/tanstack-hooks";
import {
  CloudOffIcon,
  GiftIcon,
  MonitorIcon,
  UserRoundSearchIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import PulseLoader from "@/components/PulseLoader";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import EmptyState from "@/components/EmptyState";
import SearchFilters, {
  SearchFiltersProps,
} from "@/components/filters/SearchFilters";
import FiltersDialogButton from "@/components/filters/FiltersDialogButton";

export default function SearchPage() {
  const { toast } = useToast();
  const location = useLocation();
  const [sorting, setSorting] = useState<SearchSortBy>(
    SearchSortBy.RECENTLY_ACTIVE
  );
  const { ref, inView } = useInView({
    rootMargin: "400px 0px", // Starts observing 200px before the element comes into view
    threshold: 0,
  });
  const {
    search,
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    currentSearchParams,
  } = useSearchProfiles();
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const pageRef = useRef<HTMLDivElement>(null);
  const searchTriggerFnRef = useRef<(s: SearchSortBy) => void>();
  const [isLoaded, setIsLoaded] = useState(false);
  const isStandalone = useStandaloneMode();
  const t = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialDelay(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleCardClick = () => {
    if (pageRef.current) {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
  };

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(location.pathname);
    if (savedScroll !== null) {
      const timer = setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll));
        sessionStorage.removeItem(location.pathname);
        setIsLoaded(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // If no saved scroll, we can show content immediately
      setIsLoaded(true);
    }
  }, [data]);

  // Debounced fetch next page
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isInitialDelay) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a new timeout for fetching the next page
      fetchTimeoutRef.current = setTimeout(() => {
        void fetchNextPage();
      }, 250);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isInitialDelay]);

  useEffect(() => {
    if (isError) {
      toast({
        variant: "destructive",
        title: t.SEARCH_PAGE.ERRORS.SEARCH,
        description: t.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
    }
  }, [isError, toast]);

  function handleSetSorting(newSorting: SearchSortBy) {
    setSorting(newSorting);
    searchTriggerFnRef.current?.(newSorting);
  }

  const filterProps: SearchFiltersProps = {
    sorting,
    setSorting,
    search,
    isLoading,
    currentSearchParams,
    onSearchTriggeredByParent: (fn) => {
      searchTriggerFnRef.current = fn;
    },
  };

  return (
    <div
      className={`w-full max-w-7xl dark mx-auto ${
        isStandalone ? "pb-14" : "pb-10"
      } grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-2 select-none`}
    >
      <div className="hidden lg:block w-full max-w-sm">
        <div className="sticky top-20 overflow-y-auto max-h-[calc(100vh-6rem)]">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>{t.SEARCH_PAGE.LABELS.TITLE_MOBILE}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <SearchFilters {...filterProps} />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="w-full flex flex-col gap-2">
        <div className="flex flex-row justify-between">
          <FiltersDialogButton {...filterProps} />
          <div className="w-fit items-center">
            <Select value={sorting} onValueChange={handleSetSorting}>
              <SelectTrigger className="dark rounded-xl gap-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0">
                <SelectValue placeholder="Select sorting" />
              </SelectTrigger>
              <SelectContent className="dark rounded-xl">
                <SelectGroup>
                  <SelectItem
                    className="py-3 rounded-lg"
                    value={SearchSortBy.RECENTLY_ACTIVE}
                  >
                    <span className="flex items-center gap-3">
                      <MonitorIcon className="w-4 h-4" aria-hidden="true" />
                      {t.SEARCH_PAGE.SORT_BY.RECENTLY_ACTIVE}
                    </span>
                  </SelectItem>
                  <SelectItem
                    className="py-3 rounded-lg"
                    value={SearchSortBy.NEWEST}
                  >
                    <span className="flex items-center gap-3">
                      <GiftIcon className="w-4 h-4" aria-hidden="true" />
                      {t.SEARCH_PAGE.SORT_BY.NEWEST}
                    </span>
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-[70vh] w-full mx-auto">
              <PulseLoader />
            </div>
          ) : isError ? (
            <EmptyState
              icon={CloudOffIcon}
              title={t.SEARCH_PAGE.ERRORS.SEARCH}
              message={t.SEARCH_PAGE.ERRORS.SEARCH_DESCRIPTION}
            />
          ) : (
            <>
              {!data || data.length === 0 ? (
                <EmptyState
                  icon={UserRoundSearchIcon}
                  title={t.SEARCH_PAGE.ERRORS.NO_RESULTS}
                  message={t.SEARCH_PAGE.ERRORS.NO_RESULTS_DESCRIPTION}
                />
              ) : (
                <div
                  className={`flex flex-col w-full mt-2 ${
                    isLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  ref={pageRef}
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {data.map((profile) => (
                      <ProfileCard
                        profile={profile}
                        key={profile.user_id}
                        onClick={handleCardClick}
                      />
                    ))}
                  </div>
                  <div
                    ref={ref}
                    className="w-full h-20 flex items-center justify-center"
                  >
                    {isFetchingNextPage && <Spinner />}
                  </div>
                </div>
              )}
              <ScrollTopButton />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
